import { c as _c } from "react/compiler-runtime";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useChangeProjectStatusBulkDialogContent } from "@components/table/projects/bulk-actions/bulk-change-project-status-helper";
import { useHasValidFeatureProjectLevel } from "@hooks/feature-control/use-has-user-valid-feature-project-level";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector, selectedEntitiesSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { RequiredFeatureProjectLevelName } from "@utils/feature-control/project/project-feature-control-types";
import { isFeatureEnabled } from "@utils/project-utils";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { Text, UnarchiveIcon, ArchiveIcon } from "@flat-ui";
import { UtilsTypes } from "@stellar/utils";
/** Show change project status button and dispatch action where the bulk dialog will act accordingly */
export function BulkChangeProjectStatus(t0) {
  const $ = _c(33);
  const {
    projectStatus,
    projects
  } = t0;
  const dispatch = useAppDispatch();
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectedEntitiesSelector("projects");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedEntities = useAppSelector(t1);
  const {
    hasFeatureProjectLevel
  } = useHasValidFeatureProjectLevel();
  const {
    bulkDialogContent,
    successDialogContent
  } = useChangeProjectStatusBulkDialogContent(projectStatus);
  const mode = useBulkActionsMode();
  let t2;
  let t3;
  if ($[1] !== shouldShowSuccessDialog) {
    t2 = () => {
      if (shouldShowSuccessDialog) {
        setShouldShowDialog(false);
      }
    };
    t3 = [shouldShowSuccessDialog];
    $[1] = shouldShowSuccessDialog;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== dispatch || $[5] !== hasFeatureProjectLevel || $[6] !== projectStatus || $[7] !== selectedEntities) {
    t4 = function onChangeProjectStatusClick() {
      dispatch(setBulkActionName("changeProjectStatus"));
      for (const entity of selectedEntities) {
        let isChangingProjectStatusAllowed = false;
        let hasUserValidRole = false;
        if (entity.features !== null) {
          let appropriateFeature = "project-unarchive";
          let appropriateRole = RequiredFeatureProjectLevelName.canUnarchiveProject;
          if (projectStatus === UtilsTypes.ProjectArchivingState.active) {
            appropriateFeature = "project-archive";
            appropriateRole = RequiredFeatureProjectLevelName.canArchiveProject;
          }
          isChangingProjectStatusAllowed = isFeatureEnabled(entity, appropriateFeature);
          hasUserValidRole = hasFeatureProjectLevel({
            roleName: appropriateRole,
            featuresAvailable: entity.featuresAvailable ?? []
          });
        }
        if (!hasUserValidRole || !isChangingProjectStatusAllowed) {
          dispatch(updateOne({
            id: entity.id,
            changes: {
              status: "not-allowed",
              message: "You are not allowed to change status of this project"
            }
          }));
        }
      }
      setShouldShowDialog(true);
    };
    $[4] = dispatch;
    $[5] = hasFeatureProjectLevel;
    $[6] = projectStatus;
    $[7] = selectedEntities;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const onChangeProjectStatusClick = t4;
  const isArchived = projectStatus === UtilsTypes.ProjectArchivingState.archived;
  const statusText = isArchived ? "Unarchive" : "Archive";
  const t5 = `${statusText} project`;
  let t6;
  if ($[9] !== mode || $[10] !== t5) {
    t6 = getBulkActionButtonConfig(mode, t5);
    $[9] = mode;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const config = t6;
  let t7;
  if ($[12] !== config || $[13] !== isArchived || $[14] !== onChangeProjectStatusClick || $[15] !== projects.length || $[16] !== statusText) {
    t7 = !!projects.length && <FaroTextButton onClick={onChangeProjectStatusClick} tooltipText={config.tooltipText} rippleColor={config.rippleColor} sx={config.sx} startIcon={isArchived ? <UnarchiveIcon sx={{
      width: "18px",
      height: "18px"
    }} /> : <ArchiveIcon sx={{
      width: "18px",
      height: "18px"
    }} />}>{config.shouldShowText && <Text variant="buttonM">{statusText}</Text>}</FaroTextButton>;
    $[12] = config;
    $[13] = isArchived;
    $[14] = onChangeProjectStatusClick;
    $[15] = projects.length;
    $[16] = statusText;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== bulkActionName || $[19] !== shouldShowSuccessDialog || $[20] !== successDialogContent) {
    t8 = bulkActionName === "changeProjectStatus" && shouldShowSuccessDialog && <BulkSuccessDialog dialogContent={successDialogContent} />;
    $[18] = bulkActionName;
    $[19] = shouldShowSuccessDialog;
    $[20] = successDialogContent;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== bulkActionName || $[23] !== bulkDialogContent || $[24] !== dispatch || $[25] !== projectStatus || $[26] !== selectedEntities || $[27] !== shouldShowDialog) {
    t9 = bulkActionName === "changeProjectStatus" && shouldShowDialog && <FaroBulkDialog dialogContent={bulkDialogContent} onClose={() => setShouldShowDialog(false)} trackingEvent={BulkActionEvents.bulkChangeProjectStatus} bulkActionCallback={project => dispatch(updateProjectDetails({
      projectId: project.id,
      payload: {
        archivingState: projectStatus === UtilsTypes.ProjectArchivingState.active ? "ARCHIVED" : "UNARCHIVED"
      }
    })).unwrap()} selectedEntities={selectedEntities} uniqueIdKey="id" tableDataType="projects" />;
    $[22] = bulkActionName;
    $[23] = bulkDialogContent;
    $[24] = dispatch;
    $[25] = projectStatus;
    $[26] = selectedEntities;
    $[27] = shouldShowDialog;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t10 = <>{t7}{t8}{t9}</>;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
}